/**
 * client/src/pages/security-centre/lib/protection-next-step.ts
 *
 * Pure priority-order decision: given the two live routes to Maximum
 * protection (`client/src/app/aal3-route-status.ts` — reused verbatim, no
 * fact-reading re-derived here), which ONE next-step line does the summary
 * card show? Extracted so the ordering is independently, mutation-testable.
 *
 * ORDER IS NOT ARBITRARY — it mirrors `server/logic/webauthn/step-up.ts`'s
 * OWN precedence (`beginStepUp`): the server tries the hardware basis FIRST
 * and falls back to the dual-passkey basis when no hardware-eligible
 * credential exists. So `hardware === "certified"` is checked before `twoKey`
 * here, not after — the PRIMARY action must be the one a plain tap actually
 * runs (Friction Rule clause 2).
 *
 * When BOTH routes are live, the hardware step carries `twoKeyAlso: true` so
 * the card can offer the two-passkey check as a SECONDARY, explicitly-chosen
 * option (ledger #86 — the certified key may not be in hand right now). That
 * option never runs silently: it maps to an explicit `basis: "dual"` request
 * the server validates, so its label still describes exactly what the tap
 * does. `twoKeyAlso` keys on `twoKeyWithoutCertified` — NOT on `twoKey`,
 * which counts the certified key itself: an offer that says "no key with
 * you?" and then demands that very key at its promoting leg (one-passkey-
 * plus-certified accounts; certified master) would be a dead end wearing an
 * escape hatch's label (adversarial review 2026-08-11 finding 2).
 *
 * `null` means genuinely nothing to say yet — either a read hasn't resolved
 * ("unknown"), in which case the panel must say nothing rather than guess,
 * or (structurally unreachable given the current route enums, but handled
 * rather than assumed) no branch matched.
 */
import type { Aal3RouteStatus } from "@/app/aal3-route-status";

export type ProtectionNextStep =
  | { kind: "verify-hardware"; twoKeyAlso: boolean }
  | { kind: "verify-two-key" }
  | { kind: "two-key-waiting"; matureAt: Date | undefined }
  | { kind: "add-second-passkey" }
  /**
   * #3268 review round 1 MAJOR 2: the designated master is incident-held and
   * excluded by the offerable filter (an unheld key survives) — mirrors
   * `step-up/begin.ts`'s `HELD_MASTER_USE_TRANSFER`. NEITHER basis (hardware
   * nor dual) can complete, so this pre-empts every other branch below rather
   * than offering a ceremony that would throw. The card's only honest remedy
   * is the master transfer control, never a "verify" button.
   */
  | { kind: "master-held" };

/**
 * `routes` comes straight from `useAal3RouteStatus`. Never call with
 * re-derived values — the whole point is one source of truth for what each
 * route currently is.
 */
export function resolveProtectionNextStep(
  routes: Pick<Aal3RouteStatus, "hardware" | "twoKey" | "twoKeyMatureAt" | "twoKeyWithoutCertified">,
): ProtectionNextStep | null {
  const { hardware, twoKey, twoKeyMatureAt, twoKeyWithoutCertified } = routes;
  // Either fact hasn't resolved yet — say nothing rather than offer an
  // action that might turn out to be the wrong (more expensive) one once
  // the read lands.
  if (hardware === "unknown" || twoKey === "unknown") return null;

  // #3268 review round 1 MAJOR 2: checked BEFORE every other branch — a held
  // master blocks BOTH bases outright (begin.ts is master-pinned for hardware
  // AND dual), so no "verify" action here would actually succeed. Both
  // resolvers set this together (they run the identical check over the same
  // credentials), but either alone is authoritative.
  if (hardware === "master-held" || twoKey === "master-held") return { kind: "master-held" };

  // Cheapest AND what the server will actually run first — see module header.
  // `twoKeyAlso` marks the case where the two-passkey check is ALSO live and
  // completable WITHOUT the certified key (see header), so the card can offer
  // it as a secondary explicit choice (ledger #86). Affirmative reads only —
  // an unreadable fact renders no offer.
  if (hardware === "certified") {
    return {
      kind: "verify-hardware",
      twoKeyAlso: twoKey === "available" && twoKeyWithoutCertified === true,
    };
  }

  if (twoKey === "available") return { kind: "verify-two-key" };

  if (twoKey === "waiting") return { kind: "two-key-waiting", matureAt: twoKeyMatureAt };

  if (twoKey === "needs-second-device") return { kind: "add-second-passkey" };

  // Every TwoKeyRoute case not "unknown" is handled above — this is a type
  // narrowing backstop, not a reachable branch.
  return null;
}
